import React, { useState, useEffect } from 'react';
import { 
  LineChart,
  Line,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend,
  ReferenceLine
} from 'recharts';
import { BASE_URL } from '../../../../config/api';

const timeRangeOptions = [
  { value: '1h', label: 'Last 1 Hour', seconds: 3600 },
  { value: '3h', label: 'Last 3 Hours', seconds: 10800 },
  { value: '6h', label: 'Last 6 Hours', seconds: 21600 },
  { value: '12h', label: 'Last 12 Hours', seconds: 43200 },
  { value: '24h', label: 'Last 24 Hours', seconds: 86400 },
  { value: '7d', label: 'Last 7 Days', seconds: 604800 },
];

const Metrics = ({ serviceId, clusterName, serviceName }) => {
  const [timeRange, setTimeRange] = useState('1h');
  const [metricsData, setMetricsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMetrics = async () => {
    try {
      setError(null);

      const selectedRange = timeRangeOptions.find(option => option.value === timeRange);
      const params = new URLSearchParams({
        timeRange: selectedRange.seconds.toString()
      });

      const response = await fetch(
        `https://cloudify-prod.wizr.in/${serviceId}/${clusterName}/${serviceName}/metrics?${params}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch metrics');
      }

      const data = await response.json();
      
      // Transform the data for the charts
      const transformedData = data.datapoints.map(point => ({
        timestamp: new Date(point.timestamp).toLocaleTimeString(),
        cpuUtilization: point.cpuUtilization,
        cpuAllocated: data.allocated.cpu,
        memoryUtilization: point.memoryUtilization,
        memoryAllocated: data.allocated.memory,
      }));

      setMetricsData(transformedData);
    } catch (error) {
      console.error('Error fetching metrics:', error);
      setError('Failed to load metrics. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (serviceId && clusterName && serviceName) {
      fetchMetrics();
      const interval = setInterval(fetchMetrics, 60000);
      return () => clearInterval(interval);
    }
  }, [serviceId, clusterName, serviceName, timeRange]);

  const formatXAxis = (timestamp) => {
    if (timeRange === '7d') {
      return new Date(timestamp).toLocaleDateString();
    }
    return timestamp;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-gray-900 mb-2">{label}</p>
          <div className="space-y-1">
            {payload.map((entry, index) => (
              <p 
                key={index} 
                className="text-sm flex items-center justify-between gap-4"
              >
                <span style={{ color: entry.color }}>{entry.name}:</span>
                <span className="font-medium">{entry.value.toFixed(2)}%</span>
              </p>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-600 text-sm">{error}</p>
        <button
          onClick={fetchMetrics}
          className="mt-4 inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Service Metrics</h2>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="block w-40 px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent shadow-sm transition-colors"
            >
              {timeRangeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-6">
            {/* CPU Utilization Graph */}
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-6">
              <h3 className="text-sm font-medium text-gray-900 mb-4">CPU Utilization (%)</h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={metricsData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="timestamp" 
                      tickFormatter={formatXAxis}
                      stroke="#6b7280"
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                    />
                    <YAxis 
                      unit="%" 
                      stroke="#6b7280"
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="cpuUtilization"
                      stroke="#6366f1"
                      strokeWidth={2}
                      name="CPU Usage"
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                    <ReferenceLine
                      y={100}
                      stroke="#94a3b8"
                      strokeDasharray="5 5"
                      label={{
                        value: '100% Capacity',
                        fill: '#94a3b8',
                        fontSize: 12,
                      }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Memory Utilization Graph */}
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-6">
              <h3 className="text-sm font-medium text-gray-900 mb-4">Memory Utilization (%)</h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={metricsData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="timestamp" 
                      tickFormatter={formatXAxis}
                      stroke="#6b7280"
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                    />
                    <YAxis 
                      unit="%" 
                      stroke="#6b7280"
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="memoryUtilization"
                      stroke="#10b981"
                      strokeWidth={2}
                      name="Memory Usage"
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                    <ReferenceLine
                      y={100}
                      stroke="#94a3b8"
                      strokeDasharray="5 5"
                      label={{
                        value: '100% Capacity',
                        fill: '#94a3b8',
                        fontSize: 12,
                      }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Metrics;